import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  assignUserToLabourGroup,
  removeUserFromLabourGroup,
} from '../api/userLabourGroupApi';
import { onboardingKeys } from '../../onboarding/hooks/onboardingKeys';
import { userKeys } from './userKeys';

/**
 * Mutation hook for assigning a user to a labour group.
 *
 * Errors are handled by the global QueryClient onError (shows snackbar).
 * Callers can override with mutate(data, { onSuccess, onError }).
 */
export const useAssignLabourGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, groupId }: { userId: string; groupId: number }) =>
      assignUserToLabourGroup(userId, groupId),

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(variables.userId),
      });
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.status() });
    },
  });
};

/**
 * Mutation hook for removing a user from a labour group.
 *
 * Errors are handled by the global QueryClient onError (shows snackbar).
 * Callers can override with mutate(data, { onSuccess, onError }).
 */
export const useRemoveLabourGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => removeUserFromLabourGroup(userId),

    onSuccess: (_response, userId) => {
      queryClient.invalidateQueries({ queryKey: userKeys.detail(userId) });
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
};
